import styled from '@emotion/styled/macro';
import { BaseArea, BaseButton, Variables } from '../base';
import MarkdownWrapper from '../base/markdown-wrapper/markdown-wrapper';

export const FloatingContainer = styled.div<{ isDragging: boolean }>`
    position: fixed;
    z-index: 1400;
    width: 550px;
    max-height: 650px;
    display: flex;
    flex-direction: column;
    padding: 12px 0 0 0;
    background: ${Variables.ThemeLight.Background2};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    border-radius: ${Variables.Rounded.large};
    overflow: visible;
    border: 1px solid ${Variables.ThemeLight.Border2};
    pointer-events: auto;
    opacity: ${({ isDragging }): number => (isDragging ? 0.9 : 1)};
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 12px 12px 12px;
    color: ${Variables.ThemeLight.TextMedium};
    cursor: move;
    user-select: none;
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
`;
export const HeaderGroup = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
`;
export const Title = styled.div`
    font-size: ${Variables.FontSizes.xsmall};
    font-weight: ${Variables.FontWeight.semibold};
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const CloseButton = styled(BaseButton)`
    height: auto;
    font-size: 12px;
    background: transparent;
    justify-content: flex-end;
    border: none;
    &:hover {
        color: ${Variables.Colors.n70};
    }
`;

export const Body = styled.div`
    padding: 0 0 8px 0;
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const MessagesContainer = styled.div`
    flex: 1;
    padding: 12px 16px 4px 16px;
    overflow-y: auto;
    max-height: 450px;
    background-color: ${Variables.ThemeLight.Background2};
`;

export const MessageRow = styled.div<{ role: 'user' | 'assistant' }>`
    display: flex;
    justify-content: ${({ role }): string => (role === 'user' ? 'flex-end' : 'flex-start')};
    margin-bottom: 8px;
`;

export const MessageBubble = styled.div<{ role: 'user' | 'assistant' }>`
    max-width: ${({ role }): string => (role === 'user' ? '80%' : '100%')};
    padding: 8px 12px;
    border-radius: ${Variables.Rounded.large};
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-wrap;
    background-color: ${({ role }): string => (role === 'user' ? Variables.ThemeLight.PrimaryMedium2 : 'transparent')};
    color: ${Variables.ThemeLight.TextMedium};
    box-shadow: ${({ role }): string => (role === 'user' ? '0 1px 3px rgba(15, 23, 42, 0.12)' : 'none')};
`;

export const PresetButtonsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

export const PresetButton = styled(BaseButton)`
    padding: 2px 8px;
    height: auto;
    font-size: 12px;
`;

export const AnswerContainer = styled.div`
    margin-top: 4px;
    padding: 8px;
    border-radius: 4px;
    background: ${Variables.Colors.b50};
    border: 1px solid ${Variables.Colors.b300};
    font-size: 13px;
    color: ${Variables.Colors.p500};
    white-space: pre-wrap;
    max-height: 220px;
    overflow: auto;
`;

export const SuggestedReplyContainer = styled(AnswerContainer)`
    margin-top: 8px;
    padding: 10px 12px;
`;

export const ErrorText = styled.div`
    margin-top: 4px;
    font-size: 12px;
    color: ${Variables.Colors.r50};
`;

export const Footer = styled.div`
    padding: 8px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const InputWrapper = styled.div`
    display: flex;
    align-items: flex-start;
`;

export const InputContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: flex-end;
    padding: 10px 10px 10px 14px;
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.slate300};
    border-radius: ${Variables.Rounded.xlarge};
    box-shadow: ${Variables.Shadows.medium};
`;

export const InputBox = styled.div`
    display: flex;
    flex: 1;
    align-items: flex-end;
`;

export const Textarea = styled(BaseArea)`
    flex: 1;
    font-size: 14px;
    line-height: 20px;
    background-color: transparent;
    border: 0;
    min-height: 24px;
    outline: none;
    resize: none;

    &::placeholder {
        color: ${Variables.Colors.slate400};
    }
`;

export const ActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 8px;
`;

export const IconButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 50%;
`;

export const SecondaryButton = styled(BaseButton)`
    padding: 4px 10px;
    height: auto;
    font-size: 13px;
`;
export const CustomAIButton = styled(BaseButton)`
    padding: 0;
    height: auto;
    font-size: 13px;
    background-color: ${Variables.ThemeLight.PrimaryConstant6};
    color: ${Variables.Colors.white};
    padding: 1px 6px;
    & > img {
        display: inline-block;
    }

    &:hover {
        background-color: ${Variables.ThemeLight.PrimaryInverted5};
    }
    &:active {
        background-color: ${Variables.ThemeLight.PrimaryInverted4};
    }
`;
export const AskButtonWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 20px;
    width: 120px;
    height: 60px;
    overflow: visible;

    & > * {
        position: relative;
        z-index: 1;
    }

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: inherit;
        opacity: 0;
        filter: blur(16px);
        background: radial-gradient(
            circle at center,
            ${Variables.ThemeLight.SuccessInverted9},
            ${Variables.ThemeLight.PrimaryInverted9},
            ${Variables.ThemeLight.WarningInverted9},
            ${Variables.ThemeLight.AttentionInverted9}
        );
        pointer-events: none;
        transition: opacity 0.2s ease;
        z-index: 0;
    }

    &:hover::before {
        opacity: 0.5;
    }

    &:active::before {
        opacity: 0.6;
    }
`;

export const HelpGifContainer = styled.div`
    position: absolute;
    top: 52px;
    right: 12px;
    width: 450px;
    max-height: 550px;
    background: ${Variables.ThemeLight.Background2};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: ${Variables.Rounded.large};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    z-index: 1500;
    overflow: hidden;
    pointer-events: auto;
`;

export const HelpGifTopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
`;

export const HelpGifCloseButton = styled(BaseButton)`
    height: auto;
    font-size: 12px;
    background: transparent;
    border: none;
`;

export const HelpGifImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
`;

export const MarkdownBlock = styled(MarkdownWrapper)`
    margin: 0;

    & p {
        margin: 0;
    }

    & pre {
        margin: 0;
        white-space: pre-wrap;
    }
`;
